import { ENTER } from '@angular/cdk/keycodes';
import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatLegacyAutocompleteTrigger as MatAutocompleteTrigger } from '@angular/material/legacy-autocomplete';
import { cloneDeep, isString } from 'lodash-es';
import { BehaviorSubject, combineLatest, EMPTY, Observable, of } from 'rxjs';
import { debounceTime, filter, finalize, map, startWith, tap } from 'rxjs/operators';
import {
  generateHtmlId,
  ImoTermDto, asyncClickHandlerVoid,
} from 'webcommon/shared';
import {
  ApBaseControlValueAccessor,
  BaseIdDirective, convertLoadingStatusResult,
  LoadingStatusResult,
  mapToModalSuccessValue,
  TrackLoadingEventTypes,
  trackLoadingSwitchMap,
} from 'webcommon/core-ui';
import { VersionedTerminologySearch } from 'webcommon/terminology-imo';
import { ApFindControlObjectValue, createFindControlObjectValue } from '../ApFindControlValue';

interface AutocompleteGroup {
  AdditionalRows: boolean;
  isCollapsable: boolean;
  isCollapsed: boolean;
  GroupDisabled: boolean;
  Name: string;
  ItemList: ImoTermDto[];
}

const nextId = generateHtmlId('ap-dx-findcontrol2');

// todo: combine the changes here back with other find control classes
// todo: should we actually update the autocomplete based on the input??
// todo: should this also incorporate mru, mfu, prepopulate for autocomplete
// todo: should this allow patient based filtering on the search, based on an Input??
@Component({
  selector: 'ap-dx-findcontrol2',
  templateUrl: './dx-findcontrol2.component.html',
  styleUrls: ['./dx-findcontrol2.component.less'],
  hostDirectives: [
    BaseIdDirective,
  ],
})
export class DxFindcontrol2Component
extends ApBaseControlValueAccessor<ApFindControlObjectValue<ImoTermDto>> {
  protected get disabled(): boolean {
    return this.outerControl ? this.outerControl.disabled : false;
  }

  @Input() placeHolder = '';
  @Input() singleSelect = false;

  chips: ImoTermDto[] = [];

  @ViewChild(MatAutocompleteTrigger, { static: true }) autocomplete: MatAutocompleteTrigger;

  // The value can actually be the [value] of an option right after it's selected, not just string | null.
  autoCompleteFormControl = new FormControl<string | null | ImoTermDto>(null);
  readonly waitTime = 300;

  searchedGroups$ = this.autoCompleteFormControl.valueChanges.pipe(
    debounceTime(this.waitTime),
    filter((val): val is string | null => {
      return !val || isString(val);
    }),
    // can use withLatestFrom() if we want to depend on chipsChange$ or currentlySelectedGroup$
    trackLoadingSwitchMap((value) => {
      return this.getSearchResults(value);
    }),
  );
  chipsChange$ = new BehaviorSubject<DxFindcontrol2Component['chips']>(this.chips);
  currentlySelectedGroup$ = this.chipsChange$.pipe(
    map((chips) => {
      const chipList = chips.length ? cloneDeep(chips) : [];
      this.defaultAutocompleteGroup.ItemList = chipList;
      return this.defaultAutocompleteGroup;
    }),
  );
  autocompleteGroups$ = combineLatest([
    this.searchedGroups$.pipe(
      startWith(null),
    ),
    this.currentlySelectedGroup$,
  ]).pipe(
    map(([searchedGroups, currentlySelectedGroup]): LoadingStatusResult<AutocompleteGroup[]> => {
      if (!searchedGroups) {
        return {
          eventType: TrackLoadingEventTypes.LoadedValue,
          value: [currentlySelectedGroup],
        };
      }

      return convertLoadingStatusResult(searchedGroups, (source) => {
        return [currentlySelectedGroup, ...source];
      });
    }),
  );

  defaultAutocompleteGroup: AutocompleteGroup = {
    AdditionalRows: false,
    GroupDisabled: true,
    ItemList: [],
    Name: 'Currently Selected',
    isCollapsable: true,
    isCollapsed: false,
  };

  readonly separatorKeysCodes: number[] = [ENTER];

  @ViewChild('chipInput', { static: true }) chipInput: ElementRef<HTMLInputElement>;
  @ViewChild('openModalButton', { static: true, read: ElementRef })
  openModalButton: ElementRef<HTMLElement>;

  constructor(
    private versionedTerminologySearch: VersionedTerminologySearch,
  ) {
    super();
    BaseIdDirective.setupAsHostDirective(nextId);
  }

  protected override tryAssignValueProgrammatically(_newValue: ApFindControlObjectValue<ImoTermDto>) {
    super.tryAssignValueProgrammatically(_newValue);
    this.chips = _newValue?.getAsList() || [];
    this.chipsChange$.next(this.chips);
  }

  protected chipsViewToModelUpdate(chips: ImoTermDto[]): void {
    const newValue = createFindControlObjectValue(chips);
    this.propagateChangesFromView(newValue);
  }

  protected getSearchResults(_searchString: string | null): Observable<AutocompleteGroup[]> {
    return of([]);
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  openModal = asyncClickHandlerVoid(this, () => {
    if (this.disabled) {
      return EMPTY;
    }

    // disable the autoCompleteFormControl, so it's not open or usable while the modal is open
    this.autoCompleteFormControl.disable({
      emitEvent: false,
    });
    if (this.autocomplete?.panelOpen) {
      this.autocomplete.closePanel();
    }

    const singleSelect = this.singleSelect;
    return this.versionedTerminologySearch.openModal2$(null, '', !singleSelect).pipe(
      finalize(() => {
        this.autoCompleteFormControl.enable({
          emitEvent: false,
        });
      }),
      mapToModalSuccessValue(),
      tap((results) => {
        if (singleSelect) {
          this.chips = [results[0]];
        } else {
          this.chips.push(...results);
        }
        this.chipsChange$.next(this.chips);
        this.chipsViewToModelUpdate(this.chips);
      }),
    );
  });

  getDisplayName(data: ImoTermDto): string {
    return data.Icd10Codes[0];
  }

  getChipDisplayName(data: ImoTermDto): string {
    return this.getDisplayName(data);
  }

  getAutocompleteDisplayName(data: ImoTermDto): string {
    return this.getDisplayName(data);
  }

  removeChip(object: ImoTermDto): void {
    const index = this.chips.map((chip) => chip.UniqueCode).indexOf(object.UniqueCode);

    if (index >= 0) {
      this.chips.splice(index, 1);
      this.chipsChange$.next(this.chips);
      this.chipsViewToModelUpdate(this.chips);
    }
  }

  // The current purpose of this mainly is so that when you click on a chip
  // or something in the mat-form-field that is not the actual input box, it will still focus the input box,
  // and allow you to type.
  findControlClicked(event?: Event) {
    // don't focus the input if click came from modal button
    if (event?.target instanceof Node && this.openModalButton.nativeElement.contains(event.target)) {
      return;
    }

    this.tryFocusInput();
  }

  tryFocusInput() {
    if (this.disabled) {
      return;
    }

    if (!this.chipInput) {
      return;
    }
    // Focus the input box and open the autocomplete
    this.chipInput.nativeElement.focus();
  }
}
